import { IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx';
import { AppNotification } from '@app/common';
import { NotificationControllerListReadStatusEnum, NotificationTypeEnum } from '../../defs/api';
import { notificationFromApi } from '../adapters/notification-api-adapter';
import type { RootStore } from './root-store';

export type NotificationReadStatus = NotificationControllerListReadStatusEnum;

const PAGE_LIMIT = 20;

export class NotificationHistoryStore {
    rootStore: RootStore;

    items: AppNotification[] = [];

    total = 0;

    page = 1;

    type: NotificationTypeEnum | null = null;

    portfolioId: string | null = null;

    readStatus: NotificationReadStatus | null = null;

    loading = false;

    loaded = false;

    error = false;

    private requestVersion = 0;

    private notificationRevisionDisposer: IReactionDisposer | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    get pageCount(): number {
        return Math.ceil(this.total / PAGE_LIMIT);
    }

    activate(): void {
        if (this.notificationRevisionDisposer) {
            return;
        }

        this.notificationRevisionDisposer = reaction(
            () => this.rootStore.notificationStore.revision,
            () => {
                this.fetch().catch(() => undefined);
            },
            { fireImmediately: true },
        );
    }

    deactivate(): void {
        this.notificationRevisionDisposer?.();
        this.notificationRevisionDisposer = null;
        this.requestVersion += 1;
        this.loading = false;
    }

    async setPage(page: number): Promise<void> {
        if (page === this.page || page < 1) {
            return;
        }
        this.page = page;
        await this.fetch();
    }

    async setType(type: NotificationTypeEnum | null): Promise<void> {
        if (type === this.type) {
            return;
        }
        this.type = type;
        this.page = 1;
        await this.fetch();
    }

    async setPortfolioId(portfolioId: string | null): Promise<void> {
        if (portfolioId === this.portfolioId) {
            return;
        }
        this.portfolioId = portfolioId;
        this.page = 1;
        await this.fetch();
    }

    async setReadStatus(readStatus: NotificationReadStatus | null): Promise<void> {
        if (readStatus === this.readStatus) {
            return;
        }
        this.readStatus = readStatus;
        this.page = 1;
        await this.fetch();
    }

    async fetch(): Promise<void> {
        this.requestVersion += 1;
        const { requestVersion } = this;
        const request = {
            limit: PAGE_LIMIT,
            offset: (this.page - 1) * PAGE_LIMIT,
            ...(this.type ? { type: this.type } : {}),
            ...(this.portfolioId ? { portfolioId: this.portfolioId } : {}),
            ...(this.readStatus ? { readStatus: this.readStatus } : {}),
        };

        this.loading = true;
        this.error = false;

        try {
            const result = await this.rootStore.apiClient.notificationsController.notificationControllerList(request);
            if (requestVersion !== this.requestVersion) {
                return;
            }

            const lastValidPage = Math.max(1, Math.ceil(result.total / PAGE_LIMIT));
            if (this.page > lastValidPage) {
                runInAction(() => {
                    this.page = lastValidPage;
                });
                await this.fetch();
                return;
            }

            runInAction(() => {
                this.items = result.items.map(notificationFromApi);
                this.total = result.total;
                this.loaded = true;
            });
        } catch (error) {
            if (requestVersion !== this.requestVersion) {
                return;
            }

            runInAction(() => {
                this.error = true;
                this.loaded = true;
            });
        } finally {
            if (requestVersion === this.requestVersion) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }
}
